import { MouseEventHandler } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Icon, IconNameTypes } from '../../../../libraries/wpl/foundations/src/icon';
import { LinkType } from '../../../wpl/link/src/link';

type formatLinkTextProps = {
	readonly isHaveIcon: boolean;
	readonly isCustomIcon?: boolean;
	readonly iconName?: IconNameTypes | JSX.Element;
	readonly linkText: string;
};

export type LinkWithIconProps = {
	readonly href: string;
	readonly type?: LinkType;
	readonly onClick?: MouseEventHandler;
	readonly className?: string;
	readonly isHaveIcon: boolean;
	readonly isCustomIcon?: boolean;
	readonly iconName?: IconNameTypes | JSX.Element;
	readonly linkText: string;
	readonly dataUuid?: string;
	readonly dataEventContainer?: string;
	readonly dataEventColumn?: string;
	readonly dataParentLabel?: string;
	readonly isUsePlainSpanTagForLinkText?: boolean;
	readonly spanLinkTextClassName?: string;
};

const linkWithIconStyle = css({
	alignItems: 'center',
	color: '#42526e',
	lineHeight: '1.125',
	textDecoration: 'none',
	'&:hover': {
		// eslint-disable-next-line local/ensure-wac-token-usage
		color: '#0065ff',
		textShadow: 'none',
	},
	'&:visited, &:focus': {
		textShadow: 'none',
	},
	display: 'block',
	verticalAlign: 'middle',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'svg, path': {
		// eslint-disable-next-line local/ensure-wac-token-usage
		fill: '#42526e',
	},
});

const linkIsUsePlainSpanTagForLinkTextClassStyle = css({
	alignItems: 'center',
	color: '#42526e',
	height: '26px',
	display: 'inline-flex',
	'&:hover': {
		textDecoration: 'underline',
		textShadow: 'none',
	},
	'&:visited, &:focus': {
		textShadow: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'svg, path': {
		// eslint-disable-next-line local/ensure-wac-token-usage
		fill: '#0065ff',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:visited svg path': {
		// eslint-disable-next-line local/ensure-wac-token-usage
		fill: '#403294',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover svg path, &:focus svg path': {
		// eslint-disable-next-line local/ensure-wac-token-usage
		fill: '#0049b0',
	},
	textDecoration: 'none',
	[media.below.sm]: {
		marginInlineStart: '2px',
	},
});

/**
 * Return a xs gray icon via iconNAme and linkText. This is specifically used for Global Nav header
 * This is not a WPL approved component, it's an one off. Do not use this anywhere else other than global nav
 * @param formatLinkTextProps
 * @returns
 */
const formatLinkText = ({ isHaveIcon, isCustomIcon, iconName, linkText }: formatLinkTextProps) => {
	const formatLinkStyles = [
		isHaveIcon && css({ marginBlockEnd: token('space.250') }),
		!isHaveIcon && css({ marginBlockEnd: '5px' }),
		css({
			pointerEvents: 'none',
			display: 'flex',
			alignItems: 'center',
		}),
	];
	const linkIconDivStyle = css({
		height: token('space.250'),
		width: '30px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			height: token('space.250'),
		},
	});
	const linkTextStyle = css({
		fontSize: token('space.200'),
		lineHeight: 1.5,
		margin: 0,
	});

	return (
		<div css={formatLinkStyles}>
			{isHaveIcon && (
				<div css={linkIconDivStyle}>
					{isCustomIcon ? (
						iconName
					) : (
						<Icon name={iconName as IconNameTypes} size={'xs'} color={'n500'} />
					)}
				</div>
			)}
			<p css={linkTextStyle}>{linkText}</p>
		</div>
	);
};

/**
 * Return an Anchor tag wrapped around formatLinkText specifically used for Global Nav header
 * This is not a WPL approved component, it's an one off. Do not use this anywhere else other than global nav
 * @param LinkWithIconProps
 * @returns
 */
export const LinkWithIcon = ({
	href,
	type,
	onClick,
	className,
	isHaveIcon,
	isCustomIcon,
	iconName,
	linkText,
	dataEventContainer,
	dataEventColumn,
	isUsePlainSpanTagForLinkText = false,
	spanLinkTextClassName,
}: LinkWithIconProps) => {
	return (
		<a
			className={className}
			href={href}
			target={type === 'external' ? '_blank' : ''}
			rel={type === 'external' ? 'noreferrer' : ''}
			css={[
				isUsePlainSpanTagForLinkText && linkIsUsePlainSpanTagForLinkTextClassStyle,
				!isUsePlainSpanTagForLinkText && linkWithIconStyle,
			]}
			onClick={onClick}
			data-event="clicked"
			data-event-component="linkButton"
			data-event-container={dataEventContainer}
			data-event-column={dataEventColumn}
		>
			{isUsePlainSpanTagForLinkText ? (
				<span className={spanLinkTextClassName}>{linkText}</span>
			) : (
				formatLinkText({
					isHaveIcon,
					isCustomIcon,
					iconName,
					linkText,
				})
			)}
		</a>
	);
};
